// src/components/Layout/MainLayout.js - VERSÃO FINAL E COMPLETA
import React, { useState, startTransition } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Drawer, AppBar, Toolbar, List, Typography, Divider, IconButton,
  ListItem, ListItemButton, ListItemIcon, ListItemText, Avatar, Menu, MenuItem,
  Chip, useTheme, useMediaQuery, Collapse
} from '@mui/material';
import {
  Menu as MenuIcon, Dashboard as DashboardIcon, Timeline as TimelineIcon,
  Assessment as ReportIcon, Print as PrintIcon, AccountCircle,
  Logout as LogoutIcon, BarChart as ProductivityIcon, Assignment as AssignmentIcon,
  ExpandLess, ExpandMore, TableChart as TableChartIcon, MedicationLiquid as CapsulasIcon,
  LocalCafe as SacheIcon, Factory as FactoryIcon, FactCheck as FactCheckIcon,
  ReportProblem as DivergenciaIcon, ReceiptLong as ReceiptLongIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

const drawerWidth = 280;

const MainLayout = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [submenusAbertos, setSubmenusAbertos] = useState({});
  const toggleSubmenu = (key) => setSubmenusAbertos(prev => ({ ...prev, [key]: !prev[key] }));
  const { user, logout, getNivelNome, hasPermission, NIVEIS } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
  const handleProfileMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleProfileMenuClose = () => setAnchorEl(null);
  const handleLogout = () => { logout(); };

  const getMenuItems = () => {
    const items = [];

    // Expedição (nível 1): só Linha do Tempo
    if (hasPermission(NIVEIS.EXPEDICAO)) {
      items.push({ text: 'Linha do Tempo', icon: <TimelineIcon />, path: '/linha-tempo', color: '#2196f3' });
    }
    // LAB (nível 2): visualiza Produção e Controle de Ficha
    if (hasPermission(NIVEIS.LABORATORIO)) {
      items.push({ text: 'Produção', icon: <FactoryIcon />, path: '/producao', color: '#0ea5e9' });
      items.push({ text: 'Controle de Ficha', icon: <ReceiptLongIcon />, path: '/controle-ficha', color: '#16a34a' });
    }
    // Administrador (nível 3): todo o restante.
    // Funcionários, Setores e Cadastro de Usuários viraram abas de Configuração.
    if (hasPermission(NIVEIS.ADMINISTRADOR)) {
      items.push({ text: 'Produtividade', icon: <ProductivityIcon />, path: '/produtividade', color: '#4caf50' });
      items.push({
        text: 'Relatórios',
        icon: <ReportIcon />,
        color: '#ff5722',
        submenuKey: 'relatorios',
        submenu: [
          { text: 'Relatório Tabular', icon: <TableChartIcon />, path: '/relatorio-tabular', color: '#ff5722' },
          { text: 'Relatório Cápsulas', icon: <CapsulasIcon />, path: '/relatorio-capsulas', color: '#00bcd4' },
          { text: 'Relatório Sachês', icon: <SacheIcon />, path: '/relatorio-saches', color: '#8bc34a' },
          { text: 'Produção Ordem', icon: <FactoryIcon />, path: '/relatorio-producao-ordem', color: '#7b1fa2' }
        ]
      });
      items.push({ text: 'Requisições', icon: <AssignmentIcon />, path: '/requisicoes', color: '#3b82f6' });
      items.push({ text: 'Ordem de Produção', icon: <FactoryIcon />, path: '/ordem-producao', color: '#0284c7' });
      items.push({ text: 'Conferência Farma', icon: <FactCheckIcon />, path: '/conferencia-farma', color: '#00897b' });
      items.push({ text: 'Divergências', icon: <DivergenciaIcon />, path: '/divergencias-farma', color: '#d32f2f' });
      items.push({ text: 'Impressão Crachá', icon: <PrintIcon />, path: '/impressao-cracha', color: '#795548' });
      items.push({ text: 'Configuração', icon: <SettingsIcon />, path: '/configuracoes', color: '#546e7a' });
    }
    return items;
  };
  
  const menuItems = getMenuItems();
  const currentRoute = menuItems.find(item => {
    if (item.path) return location.pathname.startsWith(item.path);
    if (item.submenu) return item.submenu.some(sub => location.pathname.startsWith(sub.path));
    return false;
  }) || menuItems.flatMap(item => item.submenu || []).find(sub => location.pathname.startsWith(sub.path));

  const drawer = (
    <Box>
      <Toolbar sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', minHeight: 80 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
          <DashboardIcon sx={{ mr: 2, fontSize: 32 }} />
          <Box>
            <Typography variant="h6" noWrap component="div" fontWeight="bold">Sistema Controle</Typography>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>Administração</Typography>
          </Box>
        </Box>
      </Toolbar>
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', p: 2, bgcolor: 'grey.50', borderRadius: 2, mb: 2 }}>
          <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>{user?.nome?.charAt(0).toUpperCase()}</Avatar>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="subtitle2" fontWeight="bold">{user?.nome}</Typography>
            <Chip label={getNivelNome(user?.nivel)} size="small" color={user?.nivel === 3 ? 'error' : user?.nivel === 2 ? 'warning' : 'info'} sx={{ mt: 0.5 }} />
          </Box>
        </Box>
      </Box>
      <Divider />
      <List sx={{ px: 1 }}>
        {menuItems.map((item) => {
          // Item com submenu
          if (item.submenu) {
            const submenuKey = item.submenuKey || item.text;
            const aberto = !!submenusAbertos[submenuKey];
            const isSubmenuActive = item.submenu.some(sub => location.pathname.startsWith(sub.path));
            return (
              <React.Fragment key={item.text}>
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => toggleSubmenu(submenuKey)}
                    sx={{ borderRadius: 2, mb: 0.5, mx: 1, '&:hover': { bgcolor: `${item.color}1A` }, ...(isSubmenuActive && { bgcolor: `${item.color}0D` }) }}
                  >
                    <ListItemIcon sx={{ color: isSubmenuActive ? item.color : 'inherit' }}>{item.icon}</ListItemIcon>
                    <ListItemText primary={item.text} primaryTypographyProps={{ fontWeight: isSubmenuActive ? 'bold' : 'normal' }} />
                    {aberto ? <ExpandLess /> : <ExpandMore />}
                  </ListItemButton>
                </ListItem>
                <Collapse in={aberto || isSubmenuActive} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {item.submenu.map((subItem) => {
                      const isSubSelected = location.pathname.startsWith(subItem.path);
                      return (
                        <ListItem key={subItem.text} disablePadding>
                          <ListItemButton
                            selected={isSubSelected}
                            onClick={() => { startTransition(() => navigate(subItem.path)); if (isMobile) setMobileOpen(false); }}
                            sx={{ borderRadius: 2, mb: 0.5, mx: 1, pl: 4, '&.Mui-selected': { bgcolor: `${subItem.color}1A`, '&:hover': { bgcolor: `${subItem.color}2A` }, '& .MuiListItemIcon-root, & .MuiListItemText-primary': { color: subItem.color, fontWeight: 'bold' } } }}
                          >
                            <ListItemIcon sx={{ color: isSubSelected ? subItem.color : 'inherit', minWidth: 36 }}>{subItem.icon}</ListItemIcon>
                            <ListItemText primary={subItem.text} primaryTypographyProps={{ fontWeight: isSubSelected ? 'bold' : 'normal', fontSize: '0.9rem' }} />
                          </ListItemButton>
                        </ListItem>
                      );
                    })}
                  </List>
                </Collapse>
              </React.Fragment>
            );
          }

          // Item normal
          const isSelected = location.pathname.startsWith(item.path);
          return (
            <ListItem key={item.text} disablePadding>
              <ListItemButton
                selected={isSelected}
                onClick={() => { startTransition(() => navigate(item.path)); if (isMobile) setMobileOpen(false); }}
                sx={{ borderRadius: 2, mb: 0.5, mx: 1, '&.Mui-selected': { bgcolor: `${item.color}1A`, '&:hover': { bgcolor: `${item.color}2A` }, '& .MuiListItemIcon-root, & .MuiListItemText-primary': { color: item.color, fontWeight: 'bold' } } }}
              >
                <ListItemIcon sx={{ color: isSelected ? item.color : 'inherit' }}>{item.icon}</ListItemIcon>
                <ListItemText primary={item.text} primaryTypographyProps={{ fontWeight: isSelected ? 'bold' : 'normal' }} />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar position="fixed" sx={{ width: { lg: `calc(100% - ${drawerWidth}px)` }, ml: { lg: `${drawerWidth}px` }, bgcolor: 'white', color: 'text.primary', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        <Toolbar>
          <IconButton color="inherit" aria-label="open drawer" edge="start" onClick={handleDrawerToggle} sx={{ mr: 2, display: { lg: 'none' } }}><MenuIcon /></IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>{currentRoute?.text || 'Dashboard'}</Typography>
          <IconButton size="large" edge="end" aria-label="account of current user" aria-haspopup="true" onClick={handleProfileMenuOpen} color="inherit"><AccountCircle /></IconButton>
          <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleProfileMenuClose} onClick={handleProfileMenuClose}>
            <MenuItem onClick={handleLogout}><ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>Sair</MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      <Box component="nav" sx={{ width: { lg: drawerWidth }, flexShrink: { lg: 0 } }}>
        <Drawer variant="temporary" open={mobileOpen} onClose={handleDrawerToggle} ModalProps={{ keepMounted: true }} sx={{ display: { xs: 'block', lg: 'none' }, '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth } }}>{drawer}</Drawer>
        <Drawer variant="permanent" sx={{ display: { xs: 'none', lg: 'block' }, '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth } }} open>{drawer}</Drawer>
      </Box>
      <Box component="main" sx={{ flexGrow: 1, p: 3, width: { lg: `calc(100% - ${drawerWidth}px)` }, minHeight: '100vh', bgcolor: 'grey.50' }}>
        <Toolbar />
        {children}
      </Box>
    </Box>
  );
};

export default MainLayout;